/**
 * financialDataService — banka hesabi ve kasa verisi icin SQL-only adaptor (Faz B / B3).
 *
 * Eski `getCompanyData(companyId, 'bank/banklist')` ve `getCompanyData(companyId, 'cashbox')`
 * (RTDB obje-map) karsiligi. Tuketiciler `Object.keys/entries` ve `data[id]` ile kullandigindan
 * adaptorler ID-anahtarli obje map dondurur (ARRAY DEGIL).
 *
 * KIMLIK: SQL integer Id (string'e cevrilmis). Banka/kasa kayitlari SQL'de oldugu icin
 *         tum sistem (Bank.tsx, Cashbox.tsx, AccountTransaction change-payment-method) bu int id'yi kullanir.
 *
 * Banka alt-hesaplari: SQL `subAccounts[]` -> RTDB-uyumlu `hesaplar{ [subId]: {...} }` map'ine cevrilir.
 *
 * KAPSAM DISI: personel borc/alacak defteri (`personnelTransactions`) ve ona bagli RTDB kasa/banka
 *              hareket YAZMALARI (AddReceivableModal/AddDebtModal/PersonnelActions) — SQL ucu yok,
 *              ayri bir alt-proje. Bu adaptor yalniz SQL OKUMA saglar.
 */

import { accountingApi } from '../../config/apiClient';
import { ACCOUNTING_API } from '../../config/api';

export interface BankSubAccountMap {
  id: string;
  accountName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  isActive: boolean;
  isPosAccount: boolean;
  isCheckAccount: boolean;
}

export interface BankAccountMapEntry {
  id: string;
  bankName: string;
  accountName: string;
  branchName: string;
  branchId: string | null;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  isActive: boolean;
  isPosAccount: boolean;
  isCheckAccount: boolean;
  /** RTDB uyumu: alt hesaplar id-anahtarli map. */
  hesaplar?: Record<string, BankSubAccountMap>;
}

export interface CashboxMapEntry {
  id: string;
  name: string;
  description: string;
  currency: string;
  balance: number;
  isActive: boolean;
  branchId: string | null;
  isDefault: boolean;
  createdAt: string;
  hasTransactions: boolean;
}

function num(v: any): number {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function mapBankAccount(data: any): BankAccountMapEntry {
  const hesaplar: Record<string, BankSubAccountMap> = {};
  if (Array.isArray(data?.subAccounts)) {
    for (const sub of data.subAccounts) {
      const subId = String(sub?.id ?? '');
      if (!subId) continue;
      hesaplar[subId] = {
        id: subId,
        accountName: sub?.accountName || '',
        accountNumber: sub?.accountNumber || '',
        iban: sub?.iban || '',
        currency: sub?.currency || 'TRY',
        balance: num(sub?.balance),
        isActive: sub?.isActive !== false,
        isPosAccount: sub?.isPosAccount || false,
        isCheckAccount: sub?.isCheckAccount || false,
      };
    }
  }
  return {
    id: String(data?.id ?? ''),
    bankName: data?.bankName || '',
    accountName: data?.accountName || '',
    branchName: data?.branchName || '',
    branchId: data?.branchId || null,
    accountNumber: data?.accountNumber || '',
    iban: data?.iban || '',
    currency: data?.currency || 'TRY',
    balance: num(data?.balance),
    isActive: data?.isActive !== false,
    isPosAccount: data?.isPosAccount || false,
    isCheckAccount: data?.isCheckAccount || false,
    hesaplar: Object.keys(hesaplar).length > 0 ? hesaplar : undefined,
  };
}

/**
 * Banka hesaplarini id-anahtarli map olarak dondurur (SADECE SQL — GET /api/BankAccount).
 * Eski `getCompanyData(companyId, 'bank/banklist')` karsiligi.
 */
export async function getBankAccountsMap(_companyId?: string): Promise<Record<string, BankAccountMapEntry>> {
  const res = await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY);
  const list = res?.data?.data ?? res?.data ?? [];
  const map: Record<string, BankAccountMapEntry> = {};
  if (Array.isArray(list)) {
    for (const item of list) {
      const entry = mapBankAccount(item);
      if (entry.id) map[entry.id] = entry;
    }
  }
  return map;
}

/**
 * Tek bir banka hesabini id ile getirir (SADECE SQL). Eski `bank/banklist/{id}` karsiligi.
 */
export async function getBankAccountById(companyId: string, id: string): Promise<BankAccountMapEntry | null> {
  const map = await getBankAccountsMap(companyId);
  return map[String(id)] ?? null;
}

/**
 * Kasalari id-anahtarli map olarak dondurur (SADECE SQL — GET /api/Cashbox).
 * Eski `getCompanyData(companyId, 'cashbox')` / `getCashbox()` karsiligi.
 */
export async function getCashboxesMap(_companyId?: string): Promise<Record<string, CashboxMapEntry>> {
  const res = await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY);
  const list = res?.data?.data ?? res?.data ?? [];
  const map: Record<string, CashboxMapEntry> = {};
  if (Array.isArray(list)) {
    for (const data of list) {
      const id = String(data?.id ?? '');
      if (!id) continue;
      map[id] = {
        id,
        name: data?.name || '',
        description: data?.description || '',
        currency: data?.currency || 'TRY',
        balance: num(data?.balance),
        isActive: data?.isActive !== false,
        branchId: data?.branchId || null,
        isDefault: data?.isDefault || false,
        createdAt: data?.createdAt || '',
        hasTransactions: data?.hasTransactions || false,
      };
    }
  }
  return map;
}

/**
 * Tek bir kasayi id ile getirir (SADECE SQL). Eski `cashbox/{id}` karsiligi.
 */
export async function getCashboxById(companyId: string, id: string): Promise<CashboxMapEntry | null> {
  const map = await getCashboxesMap(companyId);
  return map[String(id)] ?? null;
}

export interface BankVirmanPayload {
  sourceBankAccountId: number;
  sourceBankSubAccountId?: number | null;
  targetBankAccountId: number;
  targetBankSubAccountId?: number | null;
  amount: number;
  description?: string;
  documentNo?: string | null;
  createdBy?: string;
}

/**
 * Banka içi virman (ana hesap <-> alt hesap / alt hesap <-> alt hesap), atomik.
 * Eski RTDB `bank/banklist/.../balance` + `bank/transactions` çoklu-yazımının karşılığı.
 * SQL ucu: POST /api/BankAccount/virman (BankAccountManager.VirmanAsync — atomik).
 */
export async function bankVirman(payload: BankVirmanPayload): Promise<void> {
  await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNT_VIRMAN, payload);
}
